import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { NonDeprecatedKeycloakService as KeycloakService } from '@core/keycloak/non-deprecated-keycloak.service';
import { environment } from '@env/environment';
// RxJS imports are required to convert promises returned from the
// Keycloak service into observables that the interceptor can return.
import { from, switchMap } from 'rxjs';

/**
 * A simple HTTP interceptor that prefixes outgoing requests with a base URL.
 *
 * This interceptor reads the base URL from the BASE_URL injection token.
 * Requests that already specify an absolute URL are left unchanged.  You
 * can extend this interceptor to add authorization headers once
 * authentication is configured.
 */
/**
 * Interceptor that prefixes outgoing requests with the configured API base path
 * and ensures that a fresh Keycloak access token is attached to every request.
 *
 * Before forwarding a request, the interceptor calls `updateToken()` with a
 * `minValidity` window (in seconds).  This refreshes the token if it is
 * about to expire.  After the promise resolves, the interceptor reads the
 * token from the underlying Keycloak instance and adds it to the
 * `Authorization` header.  If no token is available (e.g. the user is not
 * authenticated), the request proceeds without the header.
 */
export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const baseUrl = environment.backendBasePath;
  const kc = inject(KeycloakService);
  // Prepend the API base path if the URL is relative.  Absolute URLs are left untouched.
  const url = req.url.startsWith('http') ? req.url : `${baseUrl}${req.url}`;
  // Attempt to refresh the token if it will expire within the next 30 seconds.
  // If updateToken() is undefined (older versions of KeycloakService) then
  // resolve immediately.
  const updateTokenPromise = kc.updateToken ? kc.updateToken(30) : Promise.resolve(true);
  return from(updateTokenPromise).pipe(
    switchMap(() => {
      const instance = kc.getKeycloakInstance?.();
      const token = instance?.token;
      const headers = token
        ? req.headers.set('Authorization', `Bearer ${token}`)
        : req.headers;
      const cloned = req.clone({ url, headers });
      return next(cloned);
    })
  );
};
